import { Money } from "@/components/Money";
import { ReportCard } from "@/components/reports/ReportCard";
import { pctText } from "@/lib/ranges";
import { cn } from "@/lib/utils";

const OPEX = ["cos_expenses", "salaries", "utilities", "rent", "maintenance_repairs", "transportation_logistics", "marketing_sales", "admin_office", "erp_cogs_expenses", "sales_discount"];
const OTHER = ["depreciation", "loan_interest"];

const Line = ({ row }) => {
  if (row.style === "section") return <div className="pb-1 pt-4 text-[11px] font-semibold uppercase tracking-wider text-slate-400 first:pt-0">{row.label}</div>;
  const strong = row.style !== "line";
  return (
    <div className={cn("flex items-center justify-between gap-4 py-1.5 text-sm", strong && "font-semibold text-slate-900", row.style === "total" && "border-t border-slate-300", row.style === "result" && "mt-1 rounded-md bg-slate-50 px-3 py-2")} data-testid={row.testid}>
      <span className={strong ? "" : "text-slate-600"}>{row.label}</span>
      {row.kind === "pct"
        ? <span className={cn("num", row.value < 0 && "text-red-600")}>{pctText(row.value)}</span>
        : <Money value={row.value} profit={row.style === "result"} />}
    </div>
  );
};

const lines = (keys, t, labels) => keys.filter((k) => k in t).map((k) => ({ label: labels[k], value: t[k], style: "line", testid: `rpt-line-${k}` }));

export const GrossProfitReport = ({ data, params, disabled }) => {
  const t = data.totals;
  const rows = [
    { style: "section", label: "Revenue" },
    { label: "Net sales (returns netted)", value: t.erp_sales, style: "line", testid: "rpt-line-erp_sales" },
    { label: "Total revenue", value: t.revenue, style: "total", testid: "rpt-gp-revenue" },
    { label: "Cost of goods sold (sales report Cost)", value: t.cogs, style: "line", testid: "rpt-gp-cogs" },
    { label: "Gross profit (GP Value from the sales report)", value: t.gross_profit, style: "result", testid: "rpt-gp-gross_profit" },
    { label: "Gross margin (GP ÷ revenue)", value: t.gross_margin_pct, style: "result", kind: "pct", testid: "rpt-gp-margin" },
  ];
  return (
    <ReportCard id="gross_profit" title="Gross Profit" description="Sum of GP Value from the sales report" params={params} disabled={disabled}>
      {rows.map((r, i) => <Line key={i} row={r} />)}
    </ReportCard>
  );
};

export const NetProfitReport = ({ data, params, disabled }) => {
  const t = data.totals;
  const rows = [
    { label: "Revenue", value: t.revenue, style: "total", testid: "rpt-np-revenue" },
    { label: "Cost of goods sold (sales report Cost)", value: t.cogs, style: "line", testid: "rpt-np-cogs" },
    { label: "Gross profit (GP Value from the sales report)", value: t.gross_profit, style: "result", testid: "rpt-np-gross_profit" },
    { style: "section", label: "Cost of sales expenses" },
    ...(data.cos_expense_lines || []).map((c) => ({ label: c.label, value: c.amount, style: "line", testid: `rpt-cos-${c.label}` })),
    { label: "Total cost of sales expenses", value: t.cos_expenses, style: "total", testid: "rpt-np-cos_expenses" },
    { style: "section", label: "Operating expenses" },
    ...lines(OPEX.filter((k) => k !== "cos_expenses"), t, data.labels),
    { label: "Total other operating expenses", value: Math.round((t.opex - t.cos_expenses) * 1000) / 1000, style: "total", testid: "rpt-np-opex" },
    { style: "section", label: "Depreciation and finance cost" },
    ...lines(OTHER, t, data.labels),
    { label: "Total depreciation and finance cost", value: t.other_charges, style: "total", testid: "rpt-np-other_charges" },
    { label: "Total expenses (cost of sales + all other lines)", value: t.total_expenses, style: "total", testid: "rpt-np-total_expenses" },
    { label: "Operating profit (GP − expenses before depreciation and finance cost)", value: t.operating_profit, style: "result", testid: "rpt-np-operating_profit" },
    { label: "Net profit (gross profit − total expenses)", value: t.net_profit, style: "result", testid: "rpt-np-net_profit" },
    { label: "Net margin", value: t.net_margin_pct, style: "result", kind: "pct", testid: "rpt-np-margin" },
  ];
  return (
    <ReportCard id="net_profit" title="Net Profit" description="Gross profit (GP Value) − Total expenses" params={params} disabled={disabled}>
      {rows.map((r, i) => <Line key={i} row={r} />)}
    </ReportCard>
  );
};
